<template>
  <div class="special-days text-dark">
    <div class="special-days__header">
      <div class="special-days__header__text">
        <div class="special-days__header__title-row">
          <h1>{{ $t('vue2.special_days.title') }}</h1>
          <ChangeSchoolYear :whiteTheme="true"></ChangeSchoolYear>
        </div>
        <p class="do-pill do-pill--yellow special-days__intro">Der kopieres ikke ind i datoer i klasseskemaerne med omlagte dage, og de overskrives derfor ikke. Omlagte dage kan desuden fratrække uv-timer fra klassens normale fagtimer, der skulle have lagt.</p>
      </div>
      <div class="special-days__header__actions">
        <button class="do-bttn" @click="refreshReductions"
                :disabled="!canCreate || isRefreshingReductions"
                :class="{'do-bttn--loading': isRefreshingReductions}">
          {{ $t('vue2.special_days.refresh_reductions') }}
        </button>
        <button class="do-bttn do-bttn--submit" @click="openCreate" :disabled="!canCreate"
                :content="shortcutTooltip"
                v-tippy="{arrow: true, placement: 'bottom', allowHTML: true}">
          {{ $t('vue2.special_days.create') }}
        </button>
      </div>
    </div>

    <Loader v-if="isLoading"></Loader>
    <template v-else>
      <table class="do-table do-table--striped special-days__table">
        <thead>
          <tr>
            <th>{{ $t('vue2.special_days.name') }}</th>
            <th>{{ $t('vue2.special_days.period') }}</th>
            <th class="special-days__table__classes-col">{{ $t('vue2.special_days.classes') }}</th>
            <th class="special-days__table__reduct-col">Reducer</th>
            <th class="special-days__table__actions-col"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="specialDays.length === 0">
            <td colspan="5" class="italic">{{ $t('vue2.special_days.none_created') }}</td>
          </tr>
          <tr v-for="item in specialDays" :key="item.id" class="special-days__row" @click="openEdit(item)">
            <td class="special-days__table__name">{{ item.title }}</td>
            <td>{{ formatPeriod(item) }}</td>
            <td class="special-days__table__classes-col">{{ classNames(item) }}</td>
            <td class="special-days__table__reduct-col">
              <span v-if="item.reduct" class="special-days__reduct-days">{{ reductDaysLabel(item) }}</span>
              <span v-else class="special-days__reduct-dash">–</span>
            </td>
            <td class="special-days__table__actions" @click.stop>
              <button v-if="linkedSubjects(item).length === 0"
                      class="do-bttn do-bttn--small"
                      aria-label="Opret brikker"
                      @click="openSubjects(item)"
                      v-tippy="{arrow: true, placement: 'bottom', allowHTML: true}" :content="'Opret brikker'">
                Opret brikker
              </button>
              <button v-else
                      class="do-bttn do-bttn--small"
                      @click="openSubjectsList(item, $event)"
                      v-tippy="{arrow: true, placement: 'bottom', allowHTML: true}" :content="'Se og rediger brikker'">
                {{ brikkerLabel(item) }}
              </button>
              <button class="do-bttn do-bttn--small" :aria-label="$t('vue2.special_days.edit')" @click="openEdit(item)" v-tippy="{arrow: true, placement: 'bottom', allowHTML: true}" :content="'Rediger omlagt dag'">
                <span class="icon-pencil"></span>
              </button>
              <button class="do-bttn do-bttn--small do-bttn--danger" :aria-label="$t('vue2.special_days.delete')" @click="confirmDelete(item)" v-tippy="{arrow: true, placement: 'bottom', allowHTML: true}" :content="'Slet omlagt dag'">
                <span class="icon-bin"></span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <SpecialDayModal
      v-if="isModalOpen"
      :schoolId="schoolId"
      :schoolYearId="schoolYearId"
      :specialDayId="editingId"
      :klassCalendars="klassCalendars"
      :defaultDate="schoolYearStartDate"
      @saved="onSaved"
      @close="closeModal">
    </SpecialDayModal>

    <SpecialDaySubjectsModal
      v-if="subjectsForDay"
      :schoolId="schoolId"
      :schoolYearId="schoolYearId"
      :specialDay="subjectsForDay"
      :klassCalendars="klassCalendars"
      :existingSubjects="linkedSubjects(subjectsForDay)"
      @created="onSubjectsCreated"
      @close="subjectsForDay = null">
    </SpecialDaySubjectsModal>

    <SpecialDaySubjectsListModal
      v-if="subjectsListForDay"
      :schoolId="schoolId"
      :schoolYearId="schoolYearId"
      :specialDay="subjectsListForDay"
      :klassCalendars="klassCalendars"
      @changed="onSubjectsChanged"
      @create="openSubjectsFromList"
      @close="subjectsListForDay = null">
    </SpecialDaySubjectsListModal>

    <SpecialDayCalendarModal
      v-if="isCalModalOpen"
      :schoolId="schoolId"
      :schoolYearId="schoolYearId"
      :specialDayId="editingId"
      :klassCalendars="klassCalendars"
      :schoolYearStart="schoolYearStartDate"
      :schoolYearEnd="schoolYearEndDate"
      @saved="onCalSaved"
      @close="closeCalModal">
    </SpecialDayCalendarModal>

    <ModalConfirmation
      v-if="deleting"
      :showClose="false"
      :isShowConfirmDeleteMessage="false"
      :isSubmitButtonLoading="isDeleting"
      @confirm="runDelete"
      @close="deleting = null">
      <template slot="item">
        <h3>{{ $t('vue2.special_days.confirm_delete_title') }} {{ deleting.title }}?</h3>
        <p class="special-days__confirm-text">{{ formatPeriod(deleting) }}</p>
        <div class="special-days__confirm-subjects" v-if="linkedSubjects(deleting).length > 0">
          <p class="special-days__confirm-subjects__label">{{ $t('vue2.special_days.linked_subjects') }}</p>
          <table class="special-days__confirm-subjects__table">
            <tbody>
              <tr v-for="subject in linkedSubjects(deleting)" :key="subject.id"
                  class="special-days__confirm-subjects__row"
                  :class="{'special-days__confirm-subjects__row--disabled': !canDeleteSubject(subject)}"
                  @click="toggleSubjectToDelete(subject)">
                <td class="special-days__confirm-subjects__check">
                  <input type="checkbox"
                         :checked="subjectsToDelete.indexOf(subject.id) > -1"
                         :disabled="!canDeleteSubject(subject)"
                         @click.stop
                         @change="toggleSubjectToDelete(subject)">
                </td>
                <td class="special-days__confirm-subjects__name">
                  <span class="special-days__confirm-subjects__dot" :style="{backgroundColor: subject.color}"></span>
                  {{ subject.name }}
                </td>
                <td class="special-days__confirm-subjects__note">
                  <span v-if="!canDeleteSubject(subject)">{{ $t('vue2.special_days.subject_cannot_delete') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
          <p class="special-days__confirm-subjects__hint">{{ $t('vue2.special_days.subjects_delete_hint') }}</p>
        </div>
      </template>
      <template slot="confirmText">{{ $t('vue2.special_days.delete') }}</template>
    </ModalConfirmation>
  </div>
</template>

<script>
import moment from 'moment'
import eventbus from '../../eventbus'
import docendo from '../../docendo'
import Loader from '../common/Loader'
import SpecialDayModal from './SpecialDayModal'
import SpecialDayCalendarModal from './SpecialDayCalendarModal'
import SpecialDaySubjectsModal from './SpecialDaySubjectsModal'
import SpecialDaySubjectsListModal from './SpecialDaySubjectsListModal'
import ModalConfirmation from '../Modals/ModalConfirmation'
import ChangeSchoolYear from '../School/ChangeSchoolYear'

export default {
  name: 'special-days',
  components: {Loader, SpecialDayModal, SpecialDayCalendarModal, SpecialDaySubjectsModal, SpecialDaySubjectsListModal, ModalConfirmation, ChangeSchoolYear},
  data () {
    return {
      isLoading: true,
      specialDays: [],
      isModalOpen: false,
      isCalModalOpen: false,
      subjectsForDay: null,
      subjectsListForDay: null,
      editingId: null,
      deleting: null,
      subjectsToDelete: [],
      isDeleting: false,
      isRefreshingReductions: false
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    schoolYearId () {
      return this.$store.getters.schoolYearId
    },
    klassCalendars () {
      return this.$store.getters.klassCalendars
    },
    klassCalendarsInHash () {
      return this.$store.getters.allKlassCalendarsInHash
    },
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },
    schoolYearStartDate () {
      const year = this.$store.state.localActivatedSchoolYear
      return year && year.student_time_start ? year.student_time_start : null
    },
    schoolYearEndDate () {
      const year = this.$store.state.localActivatedSchoolYear
      return year && year.student_time_end ? year.student_time_end : null
    },
    canCreate () {
      return !!this.schoolId && !!this.schoolYearId
    },
    shortcutTooltip () {
      return '<p class="tooltip-shortcut">' + this.$t('vue2.special_days.create') + '<span>N</span></p>'
    }
  },
  watch: {
    schoolYearId () {
      this.readList()
      this.loadSubjects()
    }
  },
  mounted () {
    if (this.schoolYearId) {
      this.readList()
      this.loadSubjects()
    }
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    handleKeydown (event) {
      // Kun "N" som ren genvej — ignorér ved modifier-taster, åbne modaler
      // eller når der skrives i et inputfelt.
      if (event.key !== 'n' && event.key !== 'N') {
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return
      }
      if (this.isModalOpen || this.deleting) {
        return
      }
      const el = event.target
      const tag = el && el.tagName ? el.tagName.toUpperCase() : ''
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) {
        return
      }
      if (!this.canCreate) {
        return
      }
      event.preventDefault()
      this.openCreate()
    },
    readList () {
      if (!this.schoolId || !this.schoolYearId) {
        return
      }
      this.isLoading = true
      docendo.specialDaysIndex(this.schoolId, this.schoolYearId, data => {
        this.specialDays = Array.isArray(data) ? data : []
        this.isLoading = false
      }, () => {
        this.specialDays = []
        this.isLoading = false
        eventbus.toast = {text: this.$t('vue2.special_days.error_toast'), success: false}
      })
    },
    formatPeriod (item) {
      const start = item.start_date ? moment(item.start_date) : null
      const end = item.end_date ? moment(item.end_date) : null
      if (!start) {
        return ''
      }
      if (!end || start.isSame(end, 'day')) {
        return start.format('ddd D. MMM YYYY')
      }
      return start.format('D. MMM') + ' – ' + end.format('D. MMM YYYY')
    },
    reductDays (item) {
      const start = item.start_date ? moment(item.start_date) : null
      if (!start) {
        return 0
      }
      const end = item.end_date ? moment(item.end_date) : start
      return Math.max(1, end.diff(start, 'days') + 1)
    },
    reductDaysLabel (item) {
      const days = this.reductDays(item)
      return days + ' ' + (days === 1 ? this.$t('vue2.special_days.day') : this.$t('vue2.special_days.days'))
    },
    loadSubjects () {
      if (this.schoolYearId) {
        this.$store.dispatch('readSubjects', this.schoolYearId)
      }
    },
    linkedSubjects (item) {
      const ids = (item && item.subject_ids) || []
      return ids
        .map(id => this.subjectsInHash[id])
        .filter(subject => !!subject)
    },
    classNames (item) {
      const ids = item.calendar_ids || []
      if (ids.length === 0) {
        return '–'
      }
      const names = ids
        .map(id => (this.klassCalendarsInHash[id] ? this.klassCalendarsInHash[id].name : null))
        .filter(name => !!name)
        .sort((a, b) => a.localeCompare(b, 'da', {numeric: true, sensitivity: 'base'}))
      return names.length > 0 ? names.join(', ') : ids.length
    },
    openCreate () {
      this.editingId = null
      this.isModalOpen = true
    },
    openSubjects (item) {
      this.subjectsForDay = item
    },
    onSubjectsCreated () {
      this.subjectsForDay = null
      this.readList()
      this.loadSubjects()
    },
    brikkerLabel (item) {
      const count = this.linkedSubjects(item).length
      return count + ' ' + (count === 1 ? 'brik' : 'brikker')
    },
    openSubjectsList (item, event) {
      if (event && event.currentTarget && event.currentTarget.blur) {
        event.currentTarget.blur()
      }
      this.subjectsListForDay = item
    },
    openSubjectsFromList () {
      const item = this.subjectsListForDay
      this.subjectsListForDay = null
      this.subjectsForDay = item
    },
    onSubjectsChanged () {
      docendo.specialDaysIndex(this.schoolId, this.schoolYearId, data => {
        this.specialDays = Array.isArray(data) ? data : []
        if (this.subjectsListForDay) {
          const refreshed = this.specialDays.find(day => day.id === this.subjectsListForDay.id)
          this.subjectsListForDay = refreshed || null
        }
      }, () => {})
    },
    openCalCreate () {
      this.editingId = null
      this.isCalModalOpen = true
    },
    closeCalModal () {
      this.isCalModalOpen = false
      this.editingId = null
    },
    onCalSaved () {
      this.closeCalModal()
      this.readList()
      this.updateReductions()
    },
    openEdit (item) {
      this.editingId = item.id
      this.isModalOpen = true
    },
    closeModal () {
      this.isModalOpen = false
      this.editingId = null
    },
    onSaved () {
      this.closeModal()
      this.readList()
      this.updateReductions()
    },
    updateReductions () {
      // Opdater reduktioner automatisk i baggrunden (uden toast/loader),
      // når en omlagt dag oprettes, opdateres eller slettes.
      if (!this.canCreate) {
        return
      }
      docendo.specialDaysUpdateReductions(this.schoolId, this.schoolYearId, () => {}, () => {})
    },
    refreshReductions () {
      if (!this.canCreate || this.isRefreshingReductions) {
        return
      }
      this.isRefreshingReductions = true
      docendo.specialDaysUpdateReductions(this.schoolId, this.schoolYearId, () => {
        this.isRefreshingReductions = false
        eventbus.toast = {text: this.$t('vue2.special_days.reductions_updated'), success: true}
      }, () => {
        this.isRefreshingReductions = false
        eventbus.toast = {text: this.$t('vue2.special_days.error_toast'), success: false}
      })
    },
    canDeleteSubject (subject) {
      return subject.can_delete !== false
    },
    toggleSubjectToDelete (subject) {
      if (!this.canDeleteSubject(subject)) {
        return
      }
      const idx = this.subjectsToDelete.indexOf(subject.id)
      if (idx > -1) {
        this.subjectsToDelete.splice(idx, 1)
      } else {
        this.subjectsToDelete.push(subject.id)
      }
    },
    confirmDelete (item) {
      this.deleting = item
      this.subjectsToDelete = this.linkedSubjects(item)
        .filter(subject => this.canDeleteSubject(subject))
        .map(subject => subject.id)
    },
    runDelete () {
      if (!this.deleting || this.isDeleting) {
        return
      }
      this.isDeleting = true
      const id = this.deleting.id
      const subjectIds = this.subjectsToDelete.slice()
      if (subjectIds.length === 0) {
        this.destroySpecialDay(id)
        return
      }
      docendo.bulkDeleteSubjects(this.schoolId, subjectIds, data => {
        const result = typeof data === 'string' ? JSON.parse(data) : data
        const deleted = (result && result.deleted) || []
        deleted.forEach(subject => this.$store.commit('deleteSubject', subject))
        this.destroySpecialDay(id)
      }, () => {
        this.resetDeleteState()
        eventbus.toast = {text: this.$t('vue2.special_days.error_toast'), success: false}
      })
    },
    destroySpecialDay (id) {
      docendo.specialDaysDestroy(this.schoolId, this.schoolYearId, id, () => {
        this.resetDeleteState()
        eventbus.toast = {text: this.$t('vue2.special_days.deleted_toast'), success: true}
        this.readList()
        this.updateReductions()
      }, () => {
        this.resetDeleteState()
        eventbus.toast = {text: this.$t('vue2.special_days.error_toast'), success: false}
      })
    },
    resetDeleteState () {
      this.isDeleting = false
      this.deleting = null
      this.subjectsToDelete = []
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.special-days {
  margin: 20px;
}

.special-days__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 0;
}

.special-days__header__title-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.special-days__header__text h1 {
  margin: 0;
}

.special-days__intro {
  margin: 0 0 0.75rem;
  line-height: 1.4;
  padding: 0.25rem 0.5rem;
  display: inline-block;
  font-size: 0.8rem;
  text-transform: none;
  max-width: 900px;
}

.special-days__header__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.special-days__header .do-bttn--submit {
  padding: 0 2rem;
  height: 36px;
}

.special-days__table .italic {
  font-style: italic;
  color: var(--grey);
}

.special-days__row {
  cursor: pointer;
  transition: background-color 0.15s;
}

.special-days__table__name {
  font-weight: bold;
}

.special-days__table__classes-col {
  width: 50%;
}

.special-days__table__reduct-col {
  width: 90px;
  text-align: center;
}

.special-days__reduct-days {
  white-space: nowrap;
}

.special-days__reduct-dash {
  color: var(--ligthGrey);
}

.special-days__table__brikker-col {
  width: 110px;
  text-align: center;
  white-space: nowrap;
}

.special-days__brikker-count {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  color: var(--primary);
  text-decoration: underline;
}

.special-days__brikker-count:hover {
  color: var(--primaryHover);
}

.special-days__table__actions-col {
  text-align: right;
}

.special-days__table__actions {
  text-align: right;
  white-space: nowrap;
}

.special-days__table__actions .do-bttn {
  margin-left: 0.4rem;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 8px;
  display: inline-flex;
  align-items: center;
}

.special-days__add-bttn span {
  font-size: 15px;
  line-height: 1;
  font-weight: bold;
}

.do-bttn--danger {
  color: var(--danger);
}

.special-days__confirm-text {
  padding: 0 1rem 1rem 1rem;
  margin: 0;
  color: var(--grey);
  font-size: 13px;
}

.special-days__confirm-subjects {
  padding: 0 1rem 0.5rem 1rem;
  text-align: left;
}

.special-days__confirm-subjects__label {
  margin: 0 0 0.4rem 0;
  font-size: 12px;
  font-weight: bold;
  color: var(--grey);
}

.special-days__confirm-subjects__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  max-height: 180px;
  display: block;
  overflow-y: auto;
}

.special-days__confirm-subjects__row {
  cursor: pointer;
  border-bottom: 1px solid #eef1f4;
}

.special-days__confirm-subjects__row:hover {
  background: aliceblue;
}

.special-days__confirm-subjects__row--disabled {
  cursor: default;
  color: var(--grey);
}

.special-days__confirm-subjects__row--disabled:hover {
  background: transparent;
}

.special-days__confirm-subjects__check {
  width: 28px;
  padding: 0.35rem 0.25rem;
  text-align: center;
}

.special-days__confirm-subjects__check input {
  cursor: pointer;
}

.special-days__confirm-subjects__row--disabled .special-days__confirm-subjects__check input {
  cursor: default;
}

.special-days__confirm-subjects__name {
  padding: 0.35rem 0.25rem;
  white-space: nowrap;
}

.special-days__confirm-subjects__note {
  padding: 0.35rem 0.25rem;
  text-align: right;
  font-size: 11px;
  font-style: italic;
  color: var(--grey);
  width: 100%;
}

.special-days__confirm-subjects__dot {
  display: inline-block;
  vertical-align: middle;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  margin-right: 0.5rem;
}

.special-days__confirm-subjects__hint {
  margin: 0.5rem 0 0 0;
  font-size: 11px;
  color: var(--grey);
}
</style>



// WEBPACK FOOTER //
// SpecialDays.vue?91e2e900